/*
 * audit-trail browser: filter state and request builders. Pure (no React, no I/O) so the request
 * fields (reports_enums.json request_fields_read) are pinned by unit tests.
 */
import { build_query_string } from '../overview/params';
import { month_from_search } from '../shared/month_query';
import { merge_search } from '../shared/url_query';

// reports_enums.json limits.audit_limit_default; the server caps a page at 200.
export const AUDIT_PAGE_SIZE = 100;

// reports/contract.go AuditSortKeys: = newest first, paged by before_id; a sort pages by offset.
export const AUDIT_SORTS = ['action', 'actor', 'entity', 'teacher_name', 'time'];
export const SORT_DIRS = ['asc', 'desc'];

const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;
// A deep link carries whatever was typed into the URL; only a uuid-shaped value is sent.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const valid_audit_month = (v) => typeof v === 'string' && MONTH_RE.test(v);

export const blank_audit_filters = (pay_month = '') => ({
  pay_month: valid_audit_month(pay_month) ? pay_month : '',
  teacher_uuid: '',
  teacher_name: '',
  entity_type: '',
  action: '',
  actor: ''
});

// ?month=YYYY-MM&teacher=… (Overview "All activity this month"); the legacy pay_month= and
// teacher_uuid= are still read. Anything else is dropped.
export const parse_audit_deep_link = (search) => {
  const qs = new URLSearchParams(search || '');
  const teacher_uuid = (qs.get('teacher') || qs.get('teacher_uuid') || '').trim();
  return {
    pay_month: month_from_search(search || ''),
    teacher_uuid: UUID_RE.test(teacher_uuid) ? teacher_uuid : ''
  };
};

// Facet values are enum-like (entity types, actions, cms-1 / system:month_close actor ids).
const FACET_RE = /^[A-Za-z0-9_:.-]{1,64}$/;
const facet_param = (qs, k) => {
  const v = (qs.get(k) || '').trim();
  return FACET_RE.test(v) ? v : '';
};

// The whole browser state from the URL: scope, the three narrowing picks and the column sort.
export const parse_audit_url = (search) => {
  const qs = new URLSearchParams(search || '');
  const [key, dir] = (qs.get('sort') || '').split('|');
  return {
    ...parse_audit_deep_link(search),
    entity_type: facet_param(qs, 'record'),
    action: facet_param(qs, 'action'),
    actor: facet_param(qs, 'by'),
    sort: AUDIT_SORTS.indexOf(key) !== -1 && SORT_DIRS.indexOf(dir) !== -1 ? { key, dir } : null
  };
};

const AUDIT_URL_KEYS = [
  'month',
  'teacher',
  'record',
  'action',
  'by',
  'sort',
  'pay_month',
  'teacher_uuid'
];

// The month is always written when picked: no month with a teacher means "any month" (Overview link).
export const audit_search = (search, f, sort) => {
  const own = {
    month: f.pay_month,
    teacher: f.teacher_uuid,
    record: f.entity_type,
    action: f.action,
    by: f.actor,
    sort: sort && sort.key ? `${sort.key}|${sort.dir}` : ''
  };
  return merge_search(search, AUDIT_URL_KEYS, own);
};

// A pick from the URL that the scope's facets do not offer is dropped once they load.
export const prune_to_facets = (f, facets) => {
  const has = (list, v) => !v || (list || []).some((o) => o.value === v);
  const next = {
    ...f,
    entity_type: has(facets.entity_types, f.entity_type) ? f.entity_type : '',
    action: has(facets.actions, f.action) ? f.action : '',
    actor: has(facets.actors, f.actor) ? f.actor : ''
  };
  return next.entity_type === f.entity_type && next.action === f.action && next.actor === f.actor
    ? f
    : next;
};

// The server refuses a request with neither (AUDIT_FILTER_REQUIRED); the UI never sends one.
export const has_audit_scope = (f) => !!(f && (f.pay_month || f.teacher_uuid));

// Month or teacher changed: the entity/action/actor facets are re-read, so their picks reset.
export const with_scope = (f, patch) => ({
  ...f,
  ...patch,
  entity_type: '',
  action: '',
  actor: ''
});

const scope = (f) => ({ pay_month: f.pay_month, teacher_uuid: f.teacher_uuid });

const narrowing = (f) => ({
  ...scope(f),
  entity_type: f.entity_type,
  action: f.action,
  actor: f.actor
});

export const audit_facets_params = (f) => scope(f);

const one_of = (value, allowed) => (allowed.indexOf(value) === -1 ? '' : value);

export const audit_list_params = (f, page = {}) => {
  const sort_key = page.sort ? one_of(page.sort.key, AUDIT_SORTS) : '';
  return {
    ...narrowing(f),
    before_id: sort_key ? '' : page.before_id || '',
    limit: AUDIT_PAGE_SIZE,
    sort: sort_key,
    dir: sort_key ? one_of(page.sort.dir, SORT_DIRS) || 'asc' : '',
    // Empty (never 0) when unsorted, so it drops off the wire like sort/dir (build_query_string).
    offset: sort_key ? page.offset || 0 : ''
  };
};

export const audit_csv_params = (f) => narrowing(f);

// Overview link into the browser, scoped to one teacher-month.
export const audit_link = (pay_month, teacher_uuid) =>
  `/teacher-payments/audit?${build_query_string({ month: pay_month, teacher: teacher_uuid })}`;

// Used when the server sends no usable Content-Disposition name (absent, unexposed or unsafe).
export const audit_csv_fallback_name = (f) =>
  `teacher_payments_audit_${f.pay_month || 'all_months'}${f.teacher_uuid ? `_${f.teacher_uuid.slice(-8)}` : ''}.csv`;

// Filters are "on" once they differ from the opening state (the shell's month, nothing narrowed).
export const audit_filters_on = (f, default_month = '') =>
  !!f &&
  (f.pay_month !== blank_audit_filters(default_month).pay_month ||
    !!f.teacher_uuid ||
    !!f.entity_type ||
    !!f.action ||
    !!f.actor);

export const clear_audit_filters = (default_month = '') => blank_audit_filters(default_month);

const option_label = (options, value) =>
  ((options || []).find((o) => o.value === value) || {}).label || value;

// The sentence under the filters, the Overview's shape; a keyset list says "the first N" while
// more pages remain. Null while loading or with no scope.
export const audit_filter_summary = ({
  count,
  has_more,
  filters: f,
  month_text,
  teacher_text,
  facets,
  default_month,
  loading
}) => {
  if (loading || !has_audit_scope(f)) return null;
  const noun = count === 1 ? 'entry' : 'entries';
  const fx = facets || {};
  const parts = [
    f.teacher_uuid ? `teacher ${teacher_text || 'picked'}` : 'any teacher',
    f.entity_type ? `record ${option_label(fx.entity_types, f.entity_type)}` : 'all record types',
    f.action ? `action ${option_label(fx.actions, f.action)}` : 'all actions',
    f.actor ? `by ${option_label(fx.actors, f.actor)}` : 'anyone'
  ];
  const month = f.pay_month ? month_text : 'any month';
  if (!audit_filters_on(f, default_month)) {
    return {
      lead: `${has_more ? 'the first' : 'all'} ${count} ${noun}`,
      rest: ` for ${month}, ${parts.join(', ')}.`
    };
  }
  return {
    lead: `${has_more ? 'the first ' : ''}${count} ${noun}`,
    rest: ` for ${month}: ${parts.join(', ')}.`
  };
};
